// components/project-details/EnquiryDetails.js
import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import styles from './ProjectDetails.module.css';

const EnquiryDetails = ({ enquiryDetails, projectId }) => {
  const getRingSpecs = (details) => {
    const ringDetails = details?.ringDetails;
    if (!ringDetails || Array.isArray(ringDetails) || typeof ringDetails !== 'object') {
      return {};
    }
    return ringDetails;
  };

  const formatRingPrice = (price) => {
    if (price === null || price === undefined || price === '') {
      return 'Price not specified';
    }
    if (typeof price === 'number') {
      return `£${price.toLocaleString()}`;
    }
    const priceString = price.toString().trim();
    if (!priceString) {
      return 'Price not specified';
    }
    const cleaned = priceString.replace(/[£,]/g, '');
    if (cleaned && !isNaN(cleaned)) {
      return `£${Number(cleaned).toLocaleString()}`;
    }
    return priceString.startsWith('£') ? priceString : `£${priceString}`;
  };

  const getRingSummaries = (details) => {
    const ringDetailsList = Array.isArray(details?.ringDetails)
      ? details.ringDetails
      : [];
    const featuredRing = details?.featuredRing || null;
    const featuredId = featuredRing?.ringId || featuredRing?.id || null;

    const isFeaturedMatch = (ring) => {
      if (featuredId && ring?.id) return ring.id === featuredId;
      if (featuredRing?.imageUrl && ring?.imageUrl) {
        return ring.imageUrl === featuredRing.imageUrl;
      }
      if (featuredRing?.name && ring?.title) {
        return ring.title === featuredRing.name;
      }
      return false;
    };

    const normalized = ringDetailsList.map((ring, index) => {
      const isFeatured = featuredRing ? isFeaturedMatch(ring) : false;
      const fallbackTitle = isFeatured ? featuredRing?.name : null;
      const fallbackImage = isFeatured ? featuredRing?.imageUrl : '';
      const fallbackPrice = isFeatured ? featuredRing?.price : null;
      const availablePurchase =
        typeof ring.availablePurchase === 'boolean'
          ? ring.availablePurchase
          : isFeatured
          ? featuredRing?.availablePurchase
          : undefined;

      return {
        id: ring.id || ring.ringId || `ring-${index}`,
        title: ring.title || ring.name || fallbackTitle || 'Ring reference',
        imageUrl: ring.imageUrl || fallbackImage || '',
        price: ring.price ?? fallbackPrice,
        availablePurchase,
        isFeatured
      };
    });

    if (featuredRing && !normalized.some((ring) => ring.isFeatured)) {
      normalized.unshift({
        id: featuredId || `featured-${featuredRing.name || 'ring'}`,
        title: featuredRing.name || 'Ring reference',
        imageUrl: featuredRing.imageUrl || '',
        price: featuredRing.price ?? null,
        availablePurchase: featuredRing.availablePurchase,
        isFeatured: true
      });
    }

    return normalized;
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState({
    budget: enquiryDetails?.budget || '',
    timeline: enquiryDetails?.timeline || '',
    message: enquiryDetails?.message || '',
    inspirationLinks: enquiryDetails?.inspirationLinks || [],
    ringDetails: getRingSpecs(enquiryDetails)
  });

  useEffect(() => {
    setEditedDetails({
      budget: enquiryDetails?.budget || '',
      timeline: enquiryDetails?.timeline || '',
      message: enquiryDetails?.message || '',
      inspirationLinks: enquiryDetails?.inspirationLinks || [],
      ringDetails: getRingSpecs(enquiryDetails)
    });
  }, [enquiryDetails]);

  const handleSave = async () => {
    try {
      const baseDetails = enquiryDetails || {};
      const { ringDetails: editedRingDetails, ...editedFields } = editedDetails;
      const hasRingSpecs =
        baseDetails.ringDetails &&
        !Array.isArray(baseDetails.ringDetails) &&
        typeof baseDetails.ringDetails === 'object';
      const mergedDetails = {
        ...baseDetails,
        ...editedFields
      };

      if (hasRingSpecs) {
        mergedDetails.ringDetails = editedRingDetails;
      } else if (baseDetails.ringDetails !== undefined) {
        mergedDetails.ringDetails = baseDetails.ringDetails;
      }

      await updateDoc(doc(db, 'projects', projectId), {
        enquiryDetails: mergedDetails,
        updatedAt: new Date()
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating enquiry details:', error);
      alert('Failed to update enquiry details');
    }
  };

  const handleCancel = () => {
    setEditedDetails({
      budget: enquiryDetails?.budget || '',
      timeline: enquiryDetails?.timeline || '',
      message: enquiryDetails?.message || '',
      inspirationLinks: enquiryDetails?.inspirationLinks || [],
      ringDetails: getRingSpecs(enquiryDetails)
    });
    setIsEditing(false);
  };

  const handleInspirationLinkChange = (index, value) => {
    const newLinks = [...editedDetails.inspirationLinks];
    newLinks[index] = value;
    setEditedDetails({ ...editedDetails, inspirationLinks: newLinks });
  };

  const addInspirationLink = () => {
    setEditedDetails({ 
      ...editedDetails, 
      inspirationLinks: [...editedDetails.inspirationLinks, ''] 
    });
  };

  const removeInspirationLink = (index) => {
    const newLinks = editedDetails.inspirationLinks.filter((_, i) => i !== index);
    setEditedDetails({ ...editedDetails, inspirationLinks: newLinks });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-m)' }}>
        <h2 className={styles.sectionTitle}>Enquiry Details</h2>
        {!isEditing && (
          <button onClick={() => setIsEditing(true)} className={styles.editButton}>
            Edit
          </button>
        )}
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)' }}>
        {(() => {
          const ringSummaries = getRingSummaries(enquiryDetails);
          if (ringSummaries.length === 0) {
            return null;
          }

          return (
            <div>
              <div className={styles.infoLabel} style={{ marginBottom: 'var(--spacing-xs)' }}>
                Rings referenced
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-s)' }}>
                {ringSummaries.map((ring) => (
                  <div key={ring.id} className={styles.ringCard}>
                    <div className={styles.ringCardImage}>
                      {ring.imageUrl ? (
                        <img
                          src={ring.imageUrl}
                          alt={ring.title}
                          className={styles.ringCardImageTag}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className={styles.ringCardPlaceholder}>No image</div>
                      )}
                    </div>
                    <div className={styles.ringCardDetails}>
                      <div className={styles.ringCardTitleRow}>
                        <div className={styles.ringCardTitle}>{ring.title}</div>
                        {ring.isFeatured && (
                          <span className={`${styles.ringCardBadge} ${styles.ringCardBadgeFeatured}`}>
                            Featured
                          </span>
                        )}
                        {typeof ring.availablePurchase === 'boolean' && (
                          <span
                            className={`${styles.ringCardBadge} ${
                              ring.availablePurchase
                                ? styles.ringCardBadgeAvailable
                                : styles.ringCardBadgeUnavailable
                            }`}
                          >
                            {ring.availablePurchase ? 'Available to order' : 'Previous commission'}
                          </span>
                        )}
                      </div>
                      <div className={styles.ringCardMeta}>{formatRingPrice(ring.price)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        <div className={styles.infoGrid}>
          <div>
            <div className={styles.infoLabel}>Budget</div>
            {isEditing ? (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 'var(--spacing-xxs)' }}>£</span>
                <input
                  type="number"
                  value={editedDetails.budget}
                  onChange={(e) => setEditedDetails({ ...editedDetails, budget: e.target.value })}
                  className={styles.editableField}
                  placeholder="Enter budget"
                  style={{ flex: 1 }}
                />
              </div>
            ) : (
              <div className={styles.infoValue}>
                {enquiryDetails?.budget ? `£${enquiryDetails.budget}` : 'Not specified'}
              </div>
            )}
          </div>
          <div>
            <div className={styles.infoLabel}>Timeline</div>
            {isEditing ? (
              <input
                type="text"
                value={editedDetails.timeline}
                onChange={(e) => setEditedDetails({ ...editedDetails, timeline: e.target.value })}
                className={styles.editableField}
                placeholder="e.g., 6 weeks"
              />
            ) : (
              <div className={styles.infoValue}>{enquiryDetails?.timeline || 'Not specified'}</div>
            )}
          </div>
        </div>
        
        <div>
          <div className={styles.infoLabel} style={{ marginBottom: 'var(--spacing-xs)' }}>Customer Message</div>
          {isEditing ? (
            <textarea
              value={editedDetails.message}
              onChange={(e) => setEditedDetails({ ...editedDetails, message: e.target.value })}
              className={styles.editableField}
              placeholder="Enter customer message"
              rows={3}
              style={{ resize: 'vertical' }}
            />
          ) : (
            <div className={styles.infoValue} style={{ whiteSpace: 'pre-wrap' }}>
              {enquiryDetails?.message || 'No message provided'}
            </div>
          )}
        </div>

        {(isEditing || (enquiryDetails?.inspirationLinks && enquiryDetails.inspirationLinks.length > 0)) && (
          <div>
            <div className={styles.infoLabel} style={{ marginBottom: 'var(--spacing-xs)' }}>
              Inspiration Links
            </div>
            {isEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                {editedDetails.inspirationLinks.map((link, index) => (
                  <div key={index} style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => handleInspirationLinkChange(index, e.target.value)}
                      className={styles.editableField}
                      placeholder="Enter URL"
                      style={{ flex: 1 }}
                    />
                    <button
                      onClick={() => removeInspirationLink(index)}
                      style={{
                        padding: 'var(--spacing-xs)',
                        background: 'var(--Red-100)',
                        color: 'var(--Red-600)',
                        border: 'none',
                        borderRadius: 'var(--radius-xs)',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={addInspirationLink}
                  style={{
                    padding: 'var(--spacing-xs) var(--spacing-m)',
                    background: 'var(--Grey-100)',
                    color: 'var(--Text-Primary)',
                    border: '1px dashed var(--Stroke-Secondary)',
                    borderRadius: 'var(--radius-xs)',
                    cursor: 'pointer',
                    alignSelf: 'flex-start'
                  }}
                >
                  + Add Link
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                {enquiryDetails.inspirationLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--Blue-600)',
                      textDecoration: 'none',
                      fontSize: 'var(--font-size-body4)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {link}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ring Details if present */}
        {(isEditing || enquiryDetails?.ringDetails) && Object.keys(editedDetails.ringDetails || {}).length > 0 && (
          <div>
            <div className={styles.infoLabel} style={{ marginBottom: 'var(--spacing-xs)' }}>
              Ring Specifications
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: 'var(--spacing-s)' 
            }}>
              {['size', 'metal', 'style'].map((field) => (
                <div key={field}>
                  <span style={{ 
                    fontSize: 'var(--font-size-body5)', 
                    color: 'var(--Text-Secondary)',
                    textTransform: 'capitalize' 
                  }}>
                    {field}:
                  </span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedDetails.ringDetails[field] || ''}
                      onChange={(e) => setEditedDetails({ 
                        ...editedDetails, 
                        ringDetails: { 
                          ...editedDetails.ringDetails, 
                          [field]: e.target.value 
                        }
                      })}
                      className={styles.editableField}
                      placeholder={`Enter ${field}`}
                      style={{ marginTop: 'var(--spacing-xxs)' }}
                    />
                  ) : (
                    <span style={{ marginLeft: 'var(--spacing-xs)', color: 'var(--Text-Primary)' }}>
                      {enquiryDetails.ringDetails[field] || 'Not specified'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isEditing && (
          <div style={{ display: 'flex', gap: 'var(--spacing-xs)', justifyContent: 'flex-end', marginTop: 'var(--spacing-m)' }}>
            <button
              onClick={handleCancel}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-m)',
                background: 'var(--Grey-100)',
                color: 'var(--Text-Primary)',
                border: 'none',
                borderRadius: 'var(--radius-xs)',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-m)',
                background: 'var(--Blue-600)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-xs)',
                cursor: 'pointer'
              }}
            >
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiryDetails;
